import { MouseEvent, ReactElement, ReactNode } from 'react';
/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';

import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { IconName } from 'src/components/Icon/Icon.types';
import { Heading, Text } from 'src/components/Text';

import { ICON_COMPONENT_MAP } from '../Icon/Icon.list';
import { Tooltip } from '../Tooltip';
import { emptyStateStyle } from './EmptyState.style';

export interface Props {
  border?: boolean;
  buttonAction?: () => void;
  buttonIcon?: IconName | ReactElement;
  buttonIconPosition?: 'left' | 'right';
  buttonDisabled?: boolean;
  buttonDisabledReason?: ReactNode;
  buttonSize?: 'normal' | 'tiny' | 'small';
  buttonText?: string | ReactElement;
  buttonVariant?: 'primary' | 'secondary' | 'tertiary';
  fullWidth?: boolean;
  height?: string;
  icon?: IconName;
  message?: string | ReactElement;
  shadow?: boolean;
  title?: string | ReactElement;
  whiteSpaceClickable?: boolean;
  buttonComponent?: ReactElement;
  secondaryButtonText?: ReactNode;
  secondaryButtonAction?: () => void;
  secondaryButtonIcon?: IconName;
}

export const EmptyState = (props: Props) => {
  const {
    buttonAction = () => {},
    buttonDisabled = false,
    buttonDisabledReason,
    buttonSize = 'small',
    buttonText = '',
    buttonVariant = 'secondary',
    height = '400px',
    icon,
    message = '',
    title = '',
    whiteSpaceClickable = false,
    buttonIcon,
    buttonIconPosition,
    buttonComponent,
    secondaryButtonText,
    secondaryButtonAction,
    secondaryButtonIcon,
  } = props;

  const IconComponent = icon ? ICON_COMPONENT_MAP[icon] : null;

  const handleClick = (e: MouseEvent) => {
    buttonAction?.();
  };

  const primaryButton = (
    <Button
      icon={buttonIcon}
      iconPosition={buttonIconPosition}
      variant={buttonVariant}
      size={buttonSize}
      onClick={handleClick}
      disabled={buttonDisabled}
    >
      {buttonText}
    </Button>
  );

  return (
    <div
      data-qa="emptystate"
      onClick={(e: MouseEvent) => {
        whiteSpaceClickable && handleClick(e);
      }}
      css={emptyStateStyle(props)}
    >
      <Box h={height} a="center" p="lg">
        <Box a="center" d="column">
          {IconComponent && (
            <Box w="auto" mb="xl">
              <IconComponent data-qa="emptyStateIcon" />
            </Box>
          )}
          {title && (
            <Heading tag="h6" marginBottom={buttonSize === 'normal' && !message ? 'xl' : 'lg'} align="center">
              {title}
            </Heading>
          )}
          {message && (
            <Box j="center" w={{ xs: 12, md: 8, lg: 6 }}>
              <Text align="center" marginBottom={buttonSize === 'normal' ? 'xl' : 'lg'} size="sm">
                {message}
              </Text>
            </Box>
          )}

          <Box gap="lg" j="center">
            {buttonText &&
              (buttonDisabledReason ? <Tooltip button={primaryButton}>{buttonDisabledReason}</Tooltip> : primaryButton)}

            {secondaryButtonText && (
              <Button variant="secondary" onClick={secondaryButtonAction} icon={secondaryButtonIcon}>
                {secondaryButtonText}
              </Button>
            )}
          </Box>

          {buttonComponent}
        </Box>
      </Box>
    </div>
  );
};
